<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { getUnixTime } from 'date-fns';
import { OnClickOutside } from '@vueuse/components';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import AgentAssignmentList from 'dashboard/components-next/agent/AgentAssignmentList.vue';
import ConversationMessageSearchControl from './ConversationMessageSearchControl.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import LifecycleStageSplitButton from 'dashboard/components-next/lifecycle/LifecycleStageSplitButton.vue';
import SnoozeQuickActionsMenu from 'dashboard/components-next/snooze/SnoozeQuickActionsMenu.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';
import {
  buildAgentAssignmentSections,
  fetchAgentTeamData,
  UNASSIGNED_VALUE,
} from 'dashboard/helper/agentAssignmentMenu';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const sender = computed(() => props.chat.meta.sender || {});
const senderId = computed(() => sender.value.id);
const currentContact = computed(() => {
  if (!senderId.value) {
    return sender.value;
  }

  return {
    ...sender.value,
    ...store.getters['contacts/getContact'](senderId.value),
  };
});

const assigneeName = computed(
  () => props.chat.meta?.assignee?.name || t('CONVERSATION.UNASSIGNED')
);
const currentAssignee = computed(
  () => currentChat.value?.meta?.assignee || null
);
const assigneeAvatar = computed(
  () =>
    currentAssignee.value?.thumbnail || currentAssignee.value?.avatar_url || ''
);
const isAgentMenuOpen = ref(false);
const isSnoozeMenuOpen = ref(false);

const lifecycleContact = computed(() => currentContact.value || {});
const teams = computed(() => store.getters['teams/getTeams']);
const lifecycleStages = computed(
  () => store.getters['lifecycleStages/getStages']
);
const assignableAgents = computed(() =>
  store.getters['inboxAssignableAgents/getAssignableAgents'](
    currentChat.value?.inbox_id
  )
);

const agentMenuSections = computed(() => {
  const agents = assignableAgents.value || [];
  const selectedUserId =
    currentAssignee.value?.assignee_type === 'AgentBot'
      ? null
      : currentAssignee.value?.id;
  const selectedAiAgentId =
    currentAssignee.value?.assignee_type === 'AgentBot'
      ? currentAssignee.value?.id
      : null;

  return buildAgentAssignmentSections({
    humanAgents: agents.filter(agent => agent.assignee_type !== 'AgentBot'),
    aiAgents: agents.filter(agent => agent.assignee_type === 'AgentBot'),
    teams: teams.value,
    getTeamMembers: teamId =>
      store.getters['teamMembers/getTeamMembers'](teamId) || [],
    selectedUserId,
    selectedAiAgentId,
    includeUnassigned: true,
    unassignedLabel: t('CONVERSATION.UNASSIGNED'),
    unassignedAction: 'assignAgent',
    userAction: 'assignAgent',
    aiAction: 'assignAgent',
    aiAgentsTitle: t('CHAT_LIST.OMNI_SIDEBAR.AI_AGENTS'),
    aiAgentsBadgeLabel: t('GENERAL.BETA'),
  });
});
const storeLifecycleStageId = computed(() => {
  if (!senderId.value) return null;

  const storeContact = store.getters['contacts/getContactById'](senderId.value);
  return (
    storeContact.lifecycleStageId || storeContact.lifecycle_stage_id || null
  );
});

const selectedLifecycleStageId = computed(
  () =>
    storeLifecycleStageId.value ??
    lifecycleContact.value.lifecycle_stage_id ??
    lifecycleContact.value.lifecycleStageId ??
    lifecycleContact.value.lifecycleStage?.id ??
    lifecycleContact.value.lifecycle_stage?.id ??
    null
);

const syncConversationSender = () => {
  const chatId = currentChat.value?.id;
  const contact = store.getters['contacts/getContact'](senderId.value);
  if (!chatId || !contact?.id) return;

  store.dispatch('updateConversationContact', {
    conversationId: chatId,
    ...contact,
  });
};

const updateLifecycleStage = async stageId => {
  if (!senderId.value) return;
  await store.dispatch('contacts/update', {
    id: senderId.value,
    lifecycle_stage_id: stageId,
  });
  syncConversationSender();
};

const lifecycleStageModel = computed({
  get: () => selectedLifecycleStageId.value,
  set: stageId => updateLifecycleStage(stageId),
});

const closeAgentMenu = () => {
  isAgentMenuOpen.value = false;
};

const toggleAgentMenu = () => {
  isAgentMenuOpen.value = !isAgentMenuOpen.value;
};

const closeSnoozeMenu = () => {
  isSnoozeMenuOpen.value = false;
};

const toggleSnoozeMenu = () => {
  isSnoozeMenuOpen.value = !isSnoozeMenuOpen.value;
};

const selectAgent = async ({ value, assigneeType }) => {
  await store.dispatch('assignAgent', {
    conversationId: currentChat.value.id,
    agentId: value === UNASSIGNED_VALUE ? null : value,
    assigneeType,
  });
  closeAgentMenu();
};

const selectSnoozeAction = async action => {
  const snoozedUntil = getUnixTime(action.value);

  await store.dispatch('toggleStatus', {
    conversationId: currentChat.value.id,
    status: wootConstants.STATUS_TYPE.SNOOZED,
    snoozedUntil,
  });

  closeSnoozeMenu();
};

const unsnoozeConversation = async () => {
  await store.dispatch('toggleStatus', {
    conversationId: currentChat.value.id,
    status: wootConstants.STATUS_TYPE.OPEN,
  });

  closeSnoozeMenu();
};

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const fetchAssignableAgents = inboxId => {
  if (!inboxId) return;
  store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
};

const fetchLifecycleStages = () => {
  store.dispatch('lifecycleStages/get');
};

onMounted(() => {
  fetchLifecycleStages();
  fetchAssignableAgents(currentChat.value?.inbox_id);
  fetchAgentTeamData(store);
});

watch(
  () => accountId.value,
  () => {
    fetchLifecycleStages();
  }
);

watch(
  () => currentChat.value?.inbox_id,
  inboxId => {
    fetchAssignableAgents(inboxId);
  }
);
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex h-24 w-full min-w-0 shrink-0 flex-col items-center justify-between gap-3 px-3 pb-2 pt-3 xl:h-12 xl:flex-row"
  >
    <div
      class="flex items-center justify-start gap-2 w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <div class="flex min-w-0 items-center gap-1.5 overflow-hidden">
        <Avatar
          :name="currentContact.name"
          :src="currentContact.thumbnail"
          :size="32"
          :status="currentContact.availability_status"
          hide-offline-status
          rounded-full
        />
        <div class="flex min-w-0 flex-col items-start overflow-hidden">
          <div class="flex w-full flex-row items-center gap-1 p-0 m-0">
            <span
              class="min-w-0 truncate text-sm font-medium leading-tight text-n-slate-12"
            >
              {{ currentContact.name }}
            </span>
            <span
              v-if="!isHMACVerified"
              v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
              class="i-lucide-triangle-alert my-0 mx-0 size-3.5 min-w-[14px] flex-shrink-0 text-n-amber-10"
            />
          </div>

          <div
            v-if="isSnoozed"
            class="flex items-center gap-2 overflow-hidden text-[11px] conversation--header--actions text-ellipsis whitespace-nowrap"
          >
            <span class="font-medium text-n-slate-10">
              {{ snoozedDisplayText }}
            </span>
          </div>
        </div>
      </div>
      <LifecycleStageSplitButton
        v-if="lifecycleStages.length"
        v-model="lifecycleStageModel"
        :stages="lifecycleStages"
        class="hidden shrink-0 md:flex"
      />
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <div class="hidden items-center gap-1 md:flex">
        <OnClickOutside @trigger="closeAgentMenu">
          <div class="relative">
            <button
              type="button"
              class="flex h-8 items-center gap-2 rounded-md px-2 text-sm text-n-slate-11 hover:bg-n-alpha-2"
              @click.stop="toggleAgentMenu"
            >
              <Avatar
                v-if="assigneeAvatar"
                :name="assigneeName"
                :src="assigneeAvatar"
                :status="currentAssignee?.availability_status"
                :size="20"
                rounded-full
              />
              <span v-else class="i-lucide-user-round size-5 text-n-ruby-9" />
              <span class="max-w-28 truncate">{{ assigneeName }}</span>
              <span
                class="i-lucide-chevron-down size-5 transition-transform duration-150"
                :class="{ 'rotate-180': isAgentMenuOpen }"
              />
            </button>

            <transition
              enter-active-class="transition duration-150 ease-out"
              enter-from-class="translate-y-1 scale-95 opacity-0"
              enter-to-class="translate-y-0 scale-100 opacity-100"
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="translate-y-0 scale-100 opacity-100"
              leave-to-class="translate-y-1 scale-95 opacity-0"
            >
              <div
                v-if="isAgentMenuOpen"
                class="absolute right-0 top-full z-50 mt-2 w-[272px] rounded-xl border-0 bg-n-alpha-3 shadow-lg outline outline-1 outline-n-container backdrop-blur-[100px]"
              >
                <AgentAssignmentList
                  :sections="agentMenuSections"
                  :thumbnail-size="24"
                  :search-placeholder="
                    $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
                  "
                  @select="selectAgent"
                />
              </div>
            </transition>
          </div>
        </OnClickOutside>
        <ConversationMessageSearchControl
          :conversation-id="currentChat.id"
          :inbox-id="currentChat.inbox_id"
          :contact-name="currentContact.name"
        />
        <OnClickOutside @trigger="closeSnoozeMenu">
          <div class="relative">
            <button
              v-tooltip="
                isSnoozed
                  ? $t('CONVERSATION.HEADER.UNSNOOZE_ACTION')
                  : $t('CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.TITLE')
              "
              type="button"
              class="rounded-md text-n-slate-11 transition-colors"
              :class="
                isSnoozed
                  ? 'flex h-7 items-center justify-center gap-1.5 border border-black bg-n-surface-1 px-2 text-sm font-medium leading-none shadow-sm hover:border-black hover:bg-n-surface-1'
                  : 'flex h-7 w-7 items-center justify-center leading-none hover:bg-n-alpha-2'
              "
              @click.stop="
                isSnoozed ? unsnoozeConversation() : toggleSnoozeMenu()
              "
            >
              <span
                v-if="!isSnoozed"
                class="i-lucide-timer inline-flex size-5 shrink-0"
              />
              <span
                v-else
                class="i-lucide-alarm-clock inline-flex size-4 shrink-0"
              />
              <span v-if="isSnoozed">
                {{ $t('CONVERSATION.HEADER.UNSNOOZE_ACTION') }}
              </span>
            </button>
            <transition
              enter-active-class="transition duration-150 ease-out"
              enter-from-class="translate-y-1 scale-95 opacity-0"
              enter-to-class="translate-y-0 scale-100 opacity-100"
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="translate-y-0 scale-100 opacity-100"
              leave-to-class="translate-y-1 scale-95 opacity-0"
            >
              <div
                v-if="isSnoozeMenuOpen && !isSnoozed"
                class="absolute right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border border-n-weak bg-n-surface-1 p-1.5 shadow-xl"
              >
                <SnoozeQuickActionsMenu @select="selectSnoozeAction" />
              </div>
            </transition>
          </div>
        </OnClickOutside>
        <button
          v-tooltip="'Calls coming soon'"
          type="button"
          disabled
          class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-10 leading-none"
        >
          <span class="i-lucide-phone inline-flex size-5 shrink-0" />
        </button>
      </div>
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
